<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <template v-slot:header>
                <h5 class="mb-0">{{ title }}</h5>
            </template>

            <div class="mb-3">
                <b-button variant="pgiBtn" name="backToDetailBtn" @click="goBack">
                    <i class="fa fa-arrow-left mr-2"></i>
                    Kembali
                </b-button>
            </div>

            <b-alert :show="readOnly" variant="warning">
                {{ dusWord }} ini sudah <strong>ditutup</strong> — cuma bisa dilihat, barang tidak bisa ditambah/dikeluarkan lagi.
            </b-alert>
            <b-alert :show="isOverGoldLimit" variant="danger">
                Total nominal pinjaman melebihi limit maksimal Rp. {{ $helper.addThousandSeparator(limitGoldValuePerBox) }} dengan lebih dari 1 barang. Semua {{ dusWord.toLowerCase() }} tidak bisa dikirim menggunakan metode pengiriman Paxel.
            </b-alert>

            <div class="detail-data-wrapper mb-3" v-if="hasTotalPinjaman || hasTotalBeratKering || hasTotalGrossWeight">
                <div class="d-flex flex-wrap" style="gap: 24px;">
                    <div v-if="hasTotalPinjaman">
                        <div class="smallText text-muted">Total Nominal Pinjaman</div>
                        <strong>{{ formatCurrency(totalPinjaman) }}</strong>
                    </div>
                    <div v-if="hasTotalBeratKering">
                        <div class="smallText text-muted">Total Berat Kering Emas Awal</div>
                        <strong>{{ totalBeratKering }} gram</strong>
                    </div>
                    <div v-if="hasTotalGrossWeight">
                        <div class="smallText text-muted">Total Berat Kering Emas (Input)</div>
                        <strong>{{ totalGrossWeight }} gram</strong>
                    </div>
                </div>
            </div>

            <div class="detail-data-wrapper mb-3" v-if="!readOnly">
                <h6 class="form-title mb-2">Scan Barang</h6>
                <b-input-group>
                    <b-form-input
                        v-model="scanCode"
                        :formatter="v => v.replace(/\*/g, '')"
                        name="dusScanInput"
                        placeholder="Masukkan / scan no. faktur"
                        :disabled="isScanning || !canScanDus"
                        autofocus
                        @keyup.enter="doScan"
                        @keydown.tab="e => scanCode.trim() && (e.preventDefault(), doScan())"
                    />
                    <b-input-group-append>
                        <b-button variant="cancelBtn" name="dusScanCameraBtn" :disabled="isScanning || !canScanDus" @click="openCameraScan">
                            <i class="fa fa-camera"></i>
                        </b-button>
                        <b-button variant="pgiBtn" name="dusScanBtn" :disabled="isScanning || !canScanDus" @click="doScan">
                            <b-spinner small v-if="isScanning" />
                            <i v-else class="fa fa-search"></i>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
                <b-alert :show="scanFeedback.show" :variant="scanFeedback.variant" class="mt-2 mb-0">
                    {{ scanFeedback.text }}
                </b-alert>
            </div>

            <div class="detail-data-wrapper mb-0">
                <h6 class="form-title mb-2">Barang dalam {{ dusWord }} ({{ dus.items.length }})</h6>
                <div v-if="dus.items.length == 0" class="text-muted text-center py-3">
                    Belum ada barang — scan dulu di atas.
                </div>
                <template v-else>
                <b-form-input
                    v-model="filterNoFaktur"
                    name="filterNoFaktur"
                    type="search"
                    class="mb-3"
                    placeholder="Cari no. faktur"
                />
                <div v-if="filteredItems.length == 0" class="text-muted text-center py-3">
                    Tidak ada barang dengan no. faktur tersebut.
                </div>
                <div v-else class="packing-item-list">
                    <div v-for="item in filteredItems" :key="item.noFaktur" class="packing-item mb-2">
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <strong>{{ item.merk.name }} {{ item.tipe.name }}</strong>
                                <div class="smallText text-muted">{{ item.noFaktur }} &middot; {{ item.jenis.name }}</div>
                                <div class="smallText text-muted" v-if="item.imei">IMEI: {{ item.imei }}</div>
                                <div class="smallText text-muted" v-if="item.isGold && item.beratKering != null">Berat Kering Awal: {{ item.beratKering }} gram</div>
                            </div>
                            <b-button v-if="!readOnly && canScanDus" variant="outline-danger" size="sm" @click="openModalRemoveItem(item)">
                                <i class="fa fa-times"></i>
                            </b-button>
                        </div>
                        <div v-if="item.isApple" class="apple-check-wrapper mt-2 d-flex align-items-center flex-wrap">
                            <span class="smallText font-weight-bold mr-2">Bisa Direset?</span>
                            <span>{{ item.appleCheck }}</span>
                        </div>
                        <div v-if="item.isGold" class="apple-check-wrapper mt-2 d-flex align-items-center flex-wrap">
                            <span class="smallText font-weight-bold mr-2">Berat Kering</span>
                            <span>{{ item.grossWeight }} gram</span>
                        </div>
                    </div>
                </div>
                </template>
            </div>
        </b-card>

        <b-modal id="modal-camera-scan" title="Scan Barcode/QR" centered no-close-on-backdrop @shown="startCameraScan" @hidden="stopCameraScan" hide-footer>
            <div id="qr-reader-dus" class="qr-reader-wrapper" @click="focusAt"></div>
            <div v-if="zoomRange" class="d-flex align-items-center mt-2">
                <b-button variant="cancelBtn" size="sm" class="mr-2" aria-label="Perkecil zoom" :disabled="zoomValue <= zoomRange.min" @click="stepZoom(-0.5)">
                    <i class="fa fa-minus"></i>
                </b-button>
                <b-form-input type="range" v-model="zoomValue" :min="zoomRange.min" :max="zoomRange.max" :step="zoomRange.step || 0.1" aria-label="Zoom kamera" @input="applyZoom" />
                <b-button variant="cancelBtn" size="sm" class="ml-2" aria-label="Perbesar zoom" :disabled="zoomValue >= zoomRange.max" @click="stepZoom(0.5)">
                    <i class="fa fa-plus"></i>
                </b-button>
                <span class="ml-2 text-nowrap">{{ Number(zoomValue).toFixed(1) }}x</span>
            </div>
            <b-alert :show="cameraError != ''" variant="danger" class="mt-2 mb-0">
                {{ cameraError }}
            </b-alert>
            <div class="text-right mt-2">
                <b-button variant="cancelBtn" size="sm" :disabled="isSwitchingCamera" @click="flipCamera">
                    <b-spinner small v-if="isSwitchingCamera" />
                    <span v-else><i class="fa fa-refresh mr-1"></i>Putar Kamera</span>
                </b-button>
            </div>
        </b-modal>

        <b-modal id="modal-konfirmasi-barang" title="Konfirmasi Barang" centered no-close-on-backdrop no-close-on-esc hide-footer @hidden="onHideKonfirmasiBarang">
            <div v-if="pendingItem">
                <strong>{{ pendingItem.merk.name }} {{ pendingItem.tipe.name }}</strong>
                <div class="smallText text-muted">{{ pendingItem.noFaktur }} &middot; {{ pendingItem.jenis.name }}</div>
                <div class="smallText text-muted" v-if="pendingItem.imei">IMEI: {{ pendingItem.imei }}</div>
                <div class="smallText text-muted" v-if="pendingItem.isGold && pendingItem.beratKering != null">Berat Kering Awal: {{ pendingItem.beratKering }} gram</div>
                <ModalFoto v-if="pendingItem.foto.length > 0" isButton size="sm" buttonClass="mt-2"
                    buttonLabel="Lihat Foto Barang" title="Foto Barang" :photos="pendingItem.foto" />

                <div v-if="pendingItem.isApple" class="apple-check-wrapper mt-3 d-flex align-items-center flex-wrap">
                    <span class="smallText font-weight-bold mr-2">Bisa Direset?</span>
                    <b-form-select v-model="pendingItem.appleCheck" :options="appleCheckOptions" size="sm" class="apple-check-select" />
                </div>

                <b-form-group v-if="pendingItem.isGold" :label="pendingItem.jenis.id === enumJenisEmas.LMSCAN ? 'Input gramasi sesuai dengan fisik LM Antam, dan pastikan gramasi tersebut sesuai dengan yang telah diinput pada saat awal gadai.' : 'Harap lakukan penimbangan ulang dan input sesuai hasil timbangan. Pastikan hasilnya sesuai dengan hasil penimbangan pada saat awal gadai.'" class="mt-3 mb-0">
                    <b-input-group append="Gram">
                        <b-form-input v-model="pendingItem.grossWeight" name="grossWeightInput" min="0" step="0.01"
                            @input="resetCekBeratBeda" @blur="roundGrossWeight" @wheel="$event.target.blur()" type="number" required></b-form-input>
                    </b-input-group>
                </b-form-group>

                <b-alert :show="isGrossWeightBeda" variant="warning" class="mt-3 mb-0">
                    <div class="smallText mb-2">
                        Hasil timbangan <strong>{{ pendingItem.grossWeight }} gram</strong> berbeda dari berat kering awal
                        <strong>{{ pendingItem.beratKering }} gram</strong>.
                    </div>
                    <b-form-checkbox v-model="konfirmasiBeratBeda" name="konfirmasiBeratBedaCheck">
                        <span class="smallText">Saya menyatakan penimbangan ulang ini benar saya lakukan sendiri, hasilnya sesuai fisik barang, dan sudah melaporkan ke KC.</span>
                    </b-form-checkbox>
                </b-alert>

                <div class="form-wrapper-btn text-right">
                    <b-button variant="pgiBtn" name="confirmAddToDusBtn" :disabled="!canConfirmAdd" @click="confirmAddToDus">
                        <b-spinner small v-if="isAdding" />
                        <span v-else><i class="fa fa-plus mr-2"></i>Tambah ke {{ dusWord }}</span>
                    </b-button>
                </div>
            </div>
        </b-modal>

        <ModalConfirm id="modal-confirm-remove-item" title="Hapus Barang" :isSubmitting="isRemoving" :desc="`Apakah anda yakin ingin mengeluarkan barang ini dari ${dusWord.toLowerCase()}?`" @submit="onRemoveItem" />
    </div>
</template>

<script>
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import CONSTANTS from '@/constants'
import { enumJenisEmas } from '@/shared/utils'
import ModalFoto from '@/components/modals/ModalFoto'

const SCANNER_CONFIG = {
    formatsToSupport: [
        Html5QrcodeSupportedFormats.QR_CODE,
        Html5QrcodeSupportedFormats.CODE_39,
    ],
    experimentalFeatures: { useBarCodeDetectorIfSupported: true },
    verbose: false,
}

export default {
    name: 'detail-dus',
    components: {
        ModalFoto,
    },
    data() {
        return {
            id: null,
            dusId: null,
            initStatus: 0,
            enumJenisEmas,

            dus: { dusType: '', dusNumber: null, isOpen: true, items: [] },

            scanCode: '',
            filterNoFaktur: '',
            lastDecoded: '',
            decodeStreak: 0,
            isScanning: false,
            scanFeedback: { show: false, variant: 'success', text: '' },
            scanFeedbackTimer: null,

            appleCheckOptions: ['Bisa Direset', 'Tidak Bisa Direset'],

            pendingItem: null,
            isAdding: false,

            activeRemoveItem: null,
            konfirmasiBeratBeda: false,
            isGrossWeightBeda: false,
            isRemoving: false,

            html5Qrcode: null,
            cameraError: '',
            cameraFacingMode: 'environment',
            cameraDeviceIds: { back: null, front: null },
            isSwitchingCamera: false,
            zoomRange: null,
            zoomValue: 1,

            shipmentStatus: null,
            jenisPinjaman: null,
            limitGoldValuePerBox: null,
        }
    },
    computed: {
        title() {
            return this.dus.dusType ? `${this.dus.dusType} #${this.dus.dusNumber}` : `Detail ${this.dusWord}`
        },
        readOnly() {
            return !this.dus.isOpen
        },
        filteredItems() {
            const keyword = (this.filterNoFaktur || '').trim().toLowerCase()
            if (!keyword) return this.dus.items
            return this.dus.items.filter(item => String(item.noFaktur).toLowerCase().includes(keyword))
        },
        dusWord() {
            return this.jenisPinjaman != null && this.jenisPinjaman.id == CONSTANTS.GADAI.JENIS.EMAS ? 'Food Container' : 'Dus'
        },
        canManagePacking() {
            return this.$helper.hasPermissionMenuChildren(CONSTANTS.PERMISSION.LELANG_PACKING)
        },
        canScanDus() {
            const status = CONSTANTS.LELANG_PACKING.SHIPMENT_STATUS
            return this.canManagePacking && [status.PACKING, status.DITOLAK_DROPOFF].includes(this.shipmentStatus)
        },
        canConfirmAdd() {
            if (this.isAdding) return false
            if (!this.pendingItem) return false
            if (this.pendingItem.isGold && !this.pendingItem.grossWeight) return false
            if (this.isGrossWeightBeda && !this.konfirmasiBeratBeda) return false
            return true
        },
        hasTotalPinjaman() {
            return this.dus.items.some(item => item.pinjamanPokokAwal != null)
        },
        totalPinjaman() {
            return this.dus.items.reduce((sum, item) => sum + (item.pinjamanPokokAwal || 0), 0)
        },
        isOverGoldLimit() {
            return this.limitGoldValuePerBox != null && this.dus.items.length > 1 && this.totalPinjaman > this.limitGoldValuePerBox
        },
        hasTotalBeratKering() {
            return this.dus.items.some(item => item.beratKering != null)
        },
        totalBeratKering() {
            return this.dus.items.reduce((sum, item) => sum + (Number(item.beratKering) || 0), 0).toFixed(2)
        },
        hasTotalGrossWeight() {
            return this.dus.items.some(item => item.grossWeight != null)
        },
        totalGrossWeight() {
            return this.dus.items.reduce((sum, item) => sum + (Number(item.grossWeight) || 0), 0).toFixed(2)
        },
    },
    methods: {
        goBack() {
            this.$router.push(`/lelang/persiapan-lelang/${this.id}`)
        },

        formatCurrency(value) {
            return `Rp. ${this.$helper.addThousandSeparator(Number(value || 0))}`
        },

        init() {
            this.id = this.$route.params.id
            this.dusId = this.$route.params.dusId

            let getBox = this.$axios.get(`lelang-packing/${this.id}/boxes`).then(response => {
                const box = response.data.find(item => item.boxId == this.dusId)
                if (!box) return Promise.reject(new Error('Dus tidak ditemukan.'))

                this.dus.dusType = box.boxType.name
                this.dus.dusNumber = this.$helper.dusNumber(box.boxNumber)
                this.dus.isOpen = box.isOpen
            })

            let getItems = this.$axios.get(`lelang-packing/box/${this.dusId}/items`).then(response => {
                this.dus.items = response.data.map(item => ({
                    lelangId: item.lelangId,
                    noFaktur: item.noFaktur,
                    jenis: item.jenis,
                    merk: item.merk,
                    tipe: item.tipe,
                    imei: item.imei,
                    pinjamanPokokAwal: item.pinjamanPokokAwal,
                    beratKering: item.beratKering,
                    isApple: item.isApple,
                    isGold: item.isGold,
                    appleCheck: item.appleResetSuccess ? 'Bisa Direset' : 'Tidak Bisa Direset',
                    grossWeight: item.goldGrossWeight,
                }))
            })

            let getShipmentStatus = this.$axios.get(`lelang-packing/shipment/${this.id}`).then(response => {
                this.shipmentStatus = response.data.status.id
                this.jenisPinjaman = response.data.jenisPinjaman || null
                this.limitGoldValuePerBox = response.data.limitGoldValuePerBox
            })

            Promise.all([getBox, getItems, getShipmentStatus]).then(() => {
                this.initStatus = 1
            })
            .catch(error => {
                console.log(error)
                this.initStatus = -1
            })
        },

        showFeedback(ok, text) {
            clearTimeout(this.scanFeedbackTimer)
            this.scanFeedback = { show: true, variant: ok ? 'success' : 'danger', text: text }
            this.scanFeedbackTimer = setTimeout(() => { this.scanFeedback.show = false }, 3500)
        },

        doScan() {
            const code = (this.scanCode || '').replace(/\*/g, '').trim()
            if (!code) return

            const existingItem = this.dus.items.some(item => item.noFaktur == code)

            if (existingItem) {
                this.showFeedback(false, `${code} sudah ada di ${this.dusWord.toLowerCase()} ini.`)
                this.scanCode = ''
                return
            }

            this.isScanning = true

            this.$axios.get(`lelang-packing/box/${this.dusId}/scan-preview`, { params: { noFaktur: code } })
                .then(response => {
                    const data = response.data
                    this.pendingItem = {
                        lelangId: data.lelangId,
                        noFaktur: data.noFaktur,
                        jenis: data.jenis,
                        merk: data.merk,
                        tipe: data.tipe,
                        imei: data.imei,
                        pinjamanPokokAwal: data.pinjamanPokokAwal,
                        beratKering: data.beratKering,
                        foto: data.foto || [],
                        isApple: data.isApple,
                        isGold: data.isGold,
                        appleCheck: 'Bisa Direset',
                        grossWeight: null,
                    }
                    this.$bvModal.show('modal-konfirmasi-barang')
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => {
                    this.isScanning = false
                    this.scanCode = ''
                })
        },

        onHideKonfirmasiBarang() {
            this.pendingItem = null
            this.konfirmasiBeratBeda = false
            this.isGrossWeightBeda = false
        },

        resetCekBeratBeda() {
            this.isGrossWeightBeda = false
            this.konfirmasiBeratBeda = false
        },

        roundGrossWeight() {
            if (this.pendingItem.grossWeight === '' || this.pendingItem.grossWeight == null) return
            if (Number(this.pendingItem.grossWeight) > 1000) {
                this.isGrossWeightBeda = false
                return this.$helper.toastErr(this, "Angka timbangan maksimal 1000 gram!")
            }

            this.pendingItem.grossWeight = String(parseFloat(Number(this.pendingItem.grossWeight).toFixed(2)))

            this.isGrossWeightBeda = this.pendingItem.beratKering != null
                && Number(this.pendingItem.grossWeight) != Number(this.pendingItem.beratKering)
        },

        confirmAddToDus() {
            if (!this.pendingItem) return

            if (this.pendingItem.isGold) {
                const beratKering = parseFloat(this.pendingItem.grossWeight)
                if (isNaN(beratKering) || beratKering <= 0) {
                    return this.$helper.toastErr(this, "Angka timbangan tidak valid!")
                }
                if (beratKering > 1000) {
                    return this.$helper.toastErr(this, "Angka timbangan maksimal 1000 gram!")
                }
            }

            const body = {
                noFaktur: this.pendingItem.noFaktur,
                appleResetSuccess: this.pendingItem.isApple ? this.pendingItem.appleCheck == 'Bisa Direset' : null,
                goldGrossWeight: this.pendingItem.isGold ? parseFloat(this.pendingItem.grossWeight) : null,
            }

            this.isAdding = true
            this.$axios.post(`lelang-packing/box/${this.dusId}/scan`, body).then(() => {
                this.dus.items.push(this.pendingItem)
                this.filterNoFaktur = ''
                this.showFeedback(true, `${this.pendingItem.noFaktur} (${this.pendingItem.merk.name} ${this.pendingItem.tipe.name}) masuk ${this.dusWord.toLowerCase()}.`)
                this.$bvModal.hide('modal-konfirmasi-barang')
            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => {
                this.isAdding = false
            })
        },

        openModalRemoveItem(item) {
            this.activeRemoveItem = item
            this.$bvModal.show('modal-confirm-remove-item')
        },

        onRemoveItem() {
            if (!this.activeRemoveItem) return

            const item = this.activeRemoveItem

            this.isRemoving = true
            this.$axios.post(`lelang-packing/item/${item.lelangId}/remove`).then(() => {
                this.dus.items = this.dus.items.filter(existing => existing.noFaktur != item.noFaktur)
                this.$bvModal.hide('modal-confirm-remove-item')
            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => {
                this.isRemoving = false
            })
        },

        openCameraScan() {
            this.cameraError = ''
            this.scanCode = ''
            this.lastDecoded = ''
            this.decodeStreak = 0
            this.cameraFacingMode = 'environment'
            this.$bvModal.show('modal-camera-scan')
        },

        startCameraScan() {
            this.html5Qrcode = new Html5Qrcode('qr-reader-dus', SCANNER_CONFIG)

            return this.resolveCameraDevices()
                .then(() => this.startScanner(this.cameraConstraint()))
                .catch(() => {
                    this.html5Qrcode = new Html5Qrcode('qr-reader-dus', SCANNER_CONFIG)
                    return this.startScanner({ facingMode: { exact: this.cameraFacingMode } })
                        .catch(() => {
                            this.html5Qrcode = new Html5Qrcode('qr-reader-dus', SCANNER_CONFIG)
                            return this.startScanner({ facingMode: this.cameraFacingMode })
                        })
                })
                .catch(error => {
                    this.cameraError = this.cameraErrorMessage(error)
                })
        },

        resolveCameraDevices() {
            if (this.cameraDeviceIds.back || this.cameraDeviceIds.front) return Promise.resolve()

            return Html5Qrcode.getCameras().then(cameras => {
                if (!cameras || cameras.length == 0) throw new Error('Tidak ada kamera terdeteksi')

                const back = cameras.find(camera => /back|rear|environment/i.test(camera.label))
                const front = cameras.find(camera => /front|user/i.test(camera.label))

                this.cameraDeviceIds.back = (back || cameras[cameras.length - 1]).id
                this.cameraDeviceIds.front = (front || cameras[0]).id
            })
        },

        cameraConstraint() {
            const deviceId = this.cameraFacingMode == 'environment' ? this.cameraDeviceIds.back : this.cameraDeviceIds.front

            return deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { exact: this.cameraFacingMode } }
        },

        startScanner(videoConstraints) {
            return this.html5Qrcode.start(
                videoConstraints,
                {
                    fps: 10,
                    qrbox: { width: 320, height: 240 },
                },
                decodedText => {
                    if (this.scanCode) return

                    const code = decodedText.replace(/^\*|\*$/g, '')
                    if (!/^\d{14,}$/.test(code)) {
                        this.lastDecoded = ''
                        this.decodeStreak = 0
                        return
                    }

                    if (this.lastDecoded != code) {
                        this.lastDecoded = code
                        this.decodeStreak = 1
                        return
                    }

                    this.decodeStreak++
                    if (this.decodeStreak < 3) return

                    this.scanCode = code
                    this.$bvModal.hide('modal-camera-scan')
                    this.doScan()
                },
                () => {}
            ).then(() => this.initZoom())
        },

        initZoom() {
            this.zoomRange = null
            try {
                const zoom = this.html5Qrcode.getRunningTrackCapabilities().zoom
                if (!zoom || zoom.max <= zoom.min) return
                this.zoomValue = this.html5Qrcode.getRunningTrackSettings().zoom || zoom.min
                this.zoomRange = zoom
                if (/iPhone|iPad|iPod/.test(navigator.userAgent) && this.cameraFacingMode == 'environment' && zoom.max >= 2 && this.zoomValue < 2) {
                    this.zoomValue = 2
                    this.applyZoom()
                }
            } catch (error) {
                this.zoomRange = null
            }
        },

        stepZoom(delta) {
            this.zoomValue = Math.min(this.zoomRange.max, Math.max(this.zoomRange.min, Number(this.zoomValue) + delta))
            this.applyZoom()
        },

        applyZoom() {
            this.applyCameraConstraints({ advanced: [{ zoom: Number(this.zoomValue) }] })
        },

        focusAt(event) {
            let focusModes = []
            try {
                focusModes = this.html5Qrcode.getRunningTrackCapabilities().focusMode || []
            } catch (error) {
                return
            }
            if (!focusModes.includes('single-shot') && !focusModes.includes('continuous')) return

            const rect = event.currentTarget.getBoundingClientRect()
            const point = {
                x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
                y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
            }
            const mode = focusModes.includes('single-shot') ? 'single-shot' : 'continuous'

            this.applyCameraConstraints({ advanced: [{ focusMode: mode, pointsOfInterest: [point] }, { focusMode: mode }] })
                .then(() => {
                    if (mode != 'single-shot' || !focusModes.includes('continuous')) return
                    clearTimeout(this.focusTimer)
                    this.focusTimer = setTimeout(() => this.applyCameraConstraints({ advanced: [{ focusMode: 'continuous' }] }), 3000)
                })
        },

        applyCameraConstraints(constraints) {
            try {
                return this.html5Qrcode.applyVideoConstraints(constraints).catch(() => {})
            } catch (error) {
                return Promise.resolve()
            }
        },

        cameraErrorMessage(error) {
            const name = (error && error.name) || String(error)

            if (name.includes('NotAllowedError') || name.includes('PermissionDenied')) {
                return 'Akses kamera ditolak. Izinkan kamera di pengaturan browser, lalu coba lagi.'
            }
            if (name.includes('NotFoundError') || name.includes('DevicesNotFound') || name.includes('OverconstrainedError')) {
                return 'Kamera tidak ditemukan di perangkat ini. Gunakan input manual untuk memasukkan no. faktur.'
            }
            if (name.includes('NotReadableError') || name.includes('TrackStartError')) {
                return 'Kamera sedang dipakai aplikasi lain. Tutup aplikasi tersebut, lalu coba lagi.'
            }
            if (name.includes('SecurityError')) {
                return 'Kamera hanya bisa dipakai lewat koneksi aman (https).'
            }

            return 'Kamera gagal dibuka. Coba tutup lalu buka lagi jendela scan, atau gunakan input manual.'
        },

        stopCameraScan() {
            this.zoomRange = null
            clearTimeout(this.focusTimer)
            if (!this.html5Qrcode) return

            this.html5Qrcode.stop()
                .then(() => this.html5Qrcode.clear())
                .catch(() => {})
                .finally(() => { this.html5Qrcode = null })
        },

        flipCamera() {
            this.isSwitchingCamera = true
            this.cameraFacingMode = this.cameraFacingMode == 'environment' ? 'user' : 'environment'

            const restart = () => {
                setTimeout(() => {
                    this.startCameraScan().finally(() => {
                        this.isSwitchingCamera = false
                    })
                }, 350)
            }

            if (this.html5Qrcode) {
                this.html5Qrcode.stop()
                    .then(() => this.html5Qrcode.clear())
                    .catch(() => {})
                    .finally(() => {
                        this.html5Qrcode = null
                        restart()
                    })
            } else {
                restart()
            }
        },
    },
    created() {
        this.init()
    },
}
</script>

<style scoped>
.packing-item {
    background: #f4f5f7;
    border-radius: 8px;
    padding: 10px 14px;
}
.apple-check-wrapper {
    background: #e4edf5;
    border-radius: 6px;
    padding: 6px 10px;
}
.apple-check-select {
    max-width: 220px;
}
.qr-reader-wrapper {
    width: 100%;
    min-height: 250px;
}
</style>
